// @ts-check
/**
 * Daily test API — per-student SRS review test.
 *
 * Rollout is server-side: students not on the allowlist get `{ enabled: false }`
 * from the GET, so nothing here (or in the UI) knows about student ids.
 */
import { apiRequest } from '../../../shared/api/base';

/**
 * @typedef {import('../../../shared/api/types').DailyTestResponse} DailyTestResponse
 * @typedef {import('../../../shared/api/types').DailyTestCards} DailyTestCardData
 * @typedef {import('../../../shared/api/types').DailyTestAnswersRequest} DailyTestAnswersRequest
 * @typedef {import('../../../shared/api/types').DailyTestAnswersResponse} DailyTestAnswersResponse
 * @typedef {import('../../../shared/api/types').DailyTestGateStatus} DailyTestGateStatus
 */

/**
 * A student outside the rollout gets `{ enabled: false }` and no other fields —
 * always check `enabled` before reading `cards`.
 *
 * @param {string} studentId
 * @returns {Promise<DailyTestResponse>}
 */
/** `?date=…&section=…` for whichever are given. */
function dailyTestQuery(date, section) {
  const params = [];
  if (date) params.push(`date=${encodeURIComponent(date)}`);
  if (section) params.push(`section=${encodeURIComponent(section)}`);
  return params.length ? `?${params.join('&')}` : '';
}

export function getDailyTest(studentId, date, section) {
  const q = dailyTestQuery(date, section);
  return apiRequest(`/students/${encodeURIComponent(studentId)}/daily-test${q}`);
}

/**
 * Whether this student is currently walled out of their homework until they
 * clear their daily-test debt.
 *
 * Always answers the same shape — off-rollout, un-gated and gated-but-clear are
 * all `{ gated: false, owed: 0 }`. Callers only ever ask "may they open this".
 *
 * @param {string} studentId
 * @returns {Promise<DailyTestGateStatus>}
 */
export function getDailyTestGate(studentId) {
  return apiRequest(`/students/${encodeURIComponent(studentId)}/daily-test-gate`);
}

/**
 * @param {string} studentId
 * @param {DailyTestAnswersRequest} payload
 * @returns {Promise<DailyTestAnswersResponse>}
 */
export function submitDailyTestAnswers(studentId, payload, date, section) {
  const q = dailyTestQuery(date, section);
  return apiRequest(`/students/${encodeURIComponent(studentId)}/daily-test/answers${q}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
